import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { addScaleToMoney } from '@quickbit/qb-design-system';
import {
  Pagination,
  SortOptions,
  MerchantOrderResponse,
  AccountingOrderFilter,
  SettlementReport,
  SettlementReportWithAccouting,
  AccountingReport,
  BaseSettlementReportAdjustment,
  SettlementReportAdjustment,
  SettlementPaymentDetails,
  CreateSettlementPaymentDetails,
} from './types';

export const getAccountingOrders = async ({
  pagination,
  filters,
  sortOptions,
}: {
  pagination: Pagination;
  filters: AccountingOrderFilter;
  sortOptions?: SortOptions;
}): Promise<MerchantOrderResponse> => {
  const convertedFilters = {
    ...filters,
    fromTime: filters.fromTime.toISOString(),
    toTime: filters.toTime.toISOString(),
  };

  const orderResponse = await fetchAPI<MerchantOrderResponse>(
    `v1/accounting-orders`,
    {
      method: 'POST',
      body: {
        pagination,
        filters: convertedFilters,
        sortOptions,
      },
    }
  );

  return {
    ...orderResponse,
    orders: orderResponse.orders.map(
      ({ completedAt, purchasedAt, createdAt, errors, ...rest }) => ({
        completedAt: completedAt ? new Date(completedAt) : null,
        purchasedAt: purchasedAt ? new Date(purchasedAt) : null,
        createdAt: new Date(createdAt),
        errors: errors.map((e) => ({ ...e, createdAt: new Date(e.createdAt) })),
        ...rest,
      })
    ),
  };
};

export const createSettlementReport = async ({
  filters,
}: {
  filters: AccountingOrderFilter;
}) => {
  const convertedFilters = {
    ...filters,
    fromTime: filters.fromTime.toISOString(),
    toTime: filters.toTime.toISOString(),
  };

  const reportResponse = await fetchAPI<SettlementReport>(
    `v3/settlement-reports`,
    {
      method: 'POST',
      body: {
        ...convertedFilters,
      },
    }
  );
  return reportResponse;
};

const convertToDate = (report: SettlementReport) => ({
  ...report,
  startTime: new Date(report.startTime),
  endTime: new Date(report.endTime),
});

const convertToDateAccounting = (accountingReport: AccountingReport) => ({
  ...accountingReport,
  periodStart: new Date(accountingReport.periodStart),
  periodEnd: new Date(accountingReport.periodEnd),
});

export const getSettlementReport = async (reportId: string) => {
  const report = await fetchAPI<SettlementReportWithAccouting>(
    `v3/settlement-reports/${reportId}`
  );
  const withConvertedDatesReport = convertToDate(report);
  return {
    ...withConvertedDatesReport,
    accounting: convertToDateAccounting(report.accounting),
    payments: report.payments.map(({ paidAt, createdAt, ...rest }) => ({
      paidAt: new Date(paidAt),
      createdAt: new Date(createdAt),
      ...rest,
    })),
  };
};

export const deleteSettlementReport = async (reportId: string) => {
  await fetchAPI<SettlementReport>(`v3/settlement-reports/${reportId}`, {
    method: 'DELETE',
  });
};

interface SettlementReportOverview {
  reports: SettlementReport[];
  aggregatedData: {
    count: number;
  };
}

interface SettlementReportFilters {
  merchant_ids: string[] | null;
  processed: boolean;
  processing_merchant: boolean;
}

export const getSettlementReports = async ({
  pagination,
  filters,
}: {
  pagination?: Pagination;
  filters?: SettlementReportFilters;
}) => {
  const response = await fetchAPI<SettlementReportOverview>(
    `v1/settlement-reports:search`,
    { method: 'POST', body: { pagination, filters } }
  );
  return {
    reports: response.reports.map(convertToDate),
    aggregatedData: response.aggregatedData,
  };
};

interface SettlementPaymentsResponse {
  payments: SettlementPaymentDetails[];
  aggregateData: {
    count: number;
  };
}

export interface SettlementPaymentFilters {
  merchantId?: string;
}

export const searchSettlementPayments = async ({
  pagination,
  filters,
}: {
  pagination?: Pagination;
  filters?: SettlementPaymentFilters;
}) => {
  const data = await fetchAPI<SettlementPaymentsResponse>(
    `v1/settlement-payments/search`,
    { method: 'POST', body: { pagination, filters } }
  );
  return {
    ...data,
    payments: data.payments.map(({ paidAt, createdAt, ...rest }) => ({
      createdAt: new Date(createdAt),
      paidAt: new Date(paidAt),
      ...rest,
    })),
  };
};

export const createSettlementPayment = async (
  data: CreateSettlementPaymentDetails
) => {
  const transformedData = {
    ...data,
    paidAt: data.paidAt,
    euroAmount: addScaleToMoney({
      amount: data.euroAmount,
      currencyCode: 'EUR',
    }),
  };
  return await fetchAPI<{ paymentId: string }>(`v1/settlement-payments`, {
    method: 'POST',
    body: { ...transformedData },
  });
};

export const deleteSettlementPayment = async (paymentId: string) => {
  await fetchAPI(`v1/settlement-payments/${paymentId}`, {
    method: 'DELETE',
  });
};

export const linkPaymentToReport = async (
  reportId: string,
  paymentId: string
) => {
  await fetchAPI(`v1/settlement-reports/${reportId}/link-payment`, {
    method: 'POST',
    body: { paymentId },
  });
};

export const removePaymentLinkToReport = async (
  reportId: string,
  paymentId: string
) => {
  await fetchAPI(`v1/settlement-reports/${reportId}/link-payment`, {
    method: 'DELETE',
    body: { paymentId },
  });
};

export const createSettlementReportAdjustment = async (
  reportId: string,
  data: BaseSettlementReportAdjustment
) => {
  await fetchAPI(`v1/settlement-reports/${reportId}/adjustments`, {
    method: 'POST',
    body: { ...data },
  });
};

export const updateSettlementReportAdjustment = async (
  reportId: string,
  data: SettlementReportAdjustment
) => {
  await fetchAPI(`v1/settlement-reports/${reportId}/adjustments/${data.uuid}`, {
    method: 'PUT',
    body: { ...data },
  });
};

export const updateRefundDebtAdjustment = async (
  reportId: string,
  refundDebt: number
) => {
  await fetchAPI(`v1/settlement-reports/${reportId}/refund-debt`, {
    method: 'PATCH',
    body: { refundDebt },
  });
};

export const deleteSettlementReportAdjustment = async (
  reportId: string,
  adjustmentId: string
) => {
  await fetchAPI(
    `v1/settlement-reports/${reportId}/adjustments/${adjustmentId}`,
    {
      method: 'DELETE',
    }
  );
};

export const markProcessed = async ({
  reportId,
  settlementReportPdf,
}: {
  reportId: string;
  settlementReportPdf?: string;
  merchantId?: string;
}) => {
  return await fetchAPI(`v1/settlement-reports/processed`, {
    method: 'PATCH',
    body: {
      reportId,
      settlementReportPdf,
    },
  });
};

export const emailSettlementReport = async ({
  reportId,
  settlementReportPdf,
}: {
  reportId: string;
  settlementReportPdf: string;
}) => {
  return await fetchAPI<object>(
    `v1/settlement-reports/${reportId}/email-report`,
    {
      method: 'POST',
      body: {
        reportId,
        settlementReportPdf,
      },
    }
  );
};

export const exportDAC8Report = async () => {
  const response = await fetchAPI('v1/dac-8:export', {
    method: 'POST',
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  const blob = new Blob(safeChunks, { type: 'application/xml' });
  return blob;
};
